/**
 * Уроки и тесты курса — правка на месте (D98, §11.5 ТЗ).
 *
 * До 15.09.2026 в курс из приложения добавлялся ровно один урок, вопросов
 * теста завести было нечем, а урок нельзя было ни поправить, ни удалить.
 *
 * **Результаты сотрудников не переписываются.** Урок с прохождениями не
 * удаляется, у вопроса, на который отвечали, не меняются варианты — это
 * решает сервер, экран только заранее показывает, почему кнопки нет, и
 * пишет причину отказа словами сервера.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, ErrorState, Field, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { EditorLesson, EditorQuestion, QuestionEdit } from '../api';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 8;

function problemOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Не получилось сохранить.';
}

function LessonForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: EditorLesson;
  submitLabel: string;
  onSubmit: (value: { title: string; body: string; video_url: string | null }) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [video, setVideo] = useState(initial?.video_url ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      onSubmit({ title: title.trim(), body: body.trim(), video_url: video.trim() || null }),
    onError: (error) => setProblem(problemOf(error)),
  });

  return (
    <>
      <Field label="Название урока">
        <input className="search" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Текст урока">
        <textarea
          className="search lesson-input"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <Field label="Ссылка на видео (необязательно)">
        <input className="search" value={video} onChange={(e) => setVideo(e.target.value)} />
      </Field>
      {problem && <p className="demo-notice">{problem}</p>}
      <div className="chips">
        <Button
          small
          variant="primary"
          disabled={!title.trim() || !body.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {submitLabel}
        </Button>
        <Button small variant="quiet" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </>
  );
}

function QuestionForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: EditorQuestion;
  onSubmit: (value: QuestionEdit) => Promise<unknown>;
  onCancel: () => void;
}) {
  const locked = (initial?.answers ?? 0) > 0;
  const [body, setBody] = useState(initial?.body ?? '');
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');
  const [options, setOptions] = useState<[string, boolean][]>(
    initial?.options.map((option) => [option.body, option.is_correct]) ?? [
      ['', true],
      ['', false],
    ],
  );
  const [problem, setProblem] = useState<string | null>(null);

  const correct = options.filter(([, ok]) => ok).length;
  const filled = options.every(([text]) => text.trim());
  const save = useMutation({
    mutationFn: () =>
      onSubmit({
        body: body.trim(),
        explanation: explanation.trim() || null,
        ...(locked
          ? {}
          : {
              kind: correct > 1 ? 'multi' : 'single',
              options: options.map(([text, ok]) => [text.trim(), ok] as [string, boolean]),
            }),
      }),
    onError: (error) => setProblem(problemOf(error)),
  });

  const setOption = (index: number, value: [string, boolean]) =>
    setOptions(options.map((item, at) => (at === index ? value : item)));

  return (
    <div className="question-form">
      <Field label="Вопрос">
        <input className="search" value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {locked && (
        <p className="faint">
          На вопрос уже отвечали {initial?.answers} раз — варианты менять нельзя, иначе задним
          числом изменятся результаты. Текст вопроса и пояснение править можно.
        </p>
      )}
      {options.map(([text, ok], index) => (
        <div key={index} className="chips">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={ok}
              disabled={locked}
              aria-label={`Вариант ${index + 1} правильный`}
              onChange={(e) => setOption(index, [text, e.target.checked])}
            />
          </label>
          <input
            className="search"
            value={text}
            disabled={locked}
            placeholder={`Вариант ${index + 1}`}
            onChange={(e) => setOption(index, [e.target.value, ok])}
          />
          {!locked && options.length > MIN_OPTIONS && (
            <Button
              small
              variant="quiet"
              onClick={() => setOptions(options.filter((_, at) => at !== index))}
            >
              ×
            </Button>
          )}
        </div>
      ))}
      {!locked && options.length < MAX_OPTIONS && (
        <Button small variant="quiet" onClick={() => setOptions([...options, ['', false]])}>
          + Вариант
        </Button>
      )}
      <p className="faint">Галочка — правильный вариант. Несколько галочек — несколько ответов.</p>
      <Field label="Пояснение для разбора ошибок (необязательно)">
        <input
          className="search"
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>
      {problem && <p className="demo-notice">{problem}</p>}
      <div className="chips">
        <Button
          small
          variant="primary"
          disabled={!body.trim() || (!locked && (correct === 0 || !filled)) || save.isPending}
          onClick={() => save.mutate()}
        >
          Сохранить вопрос
        </Button>
        <Button small variant="quiet" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function LessonBlock({
  lesson,
  number,
  refresh,
}: {
  lesson: EditorLesson;
  number: number;
  refresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const removeLesson = useMutation({
    mutationFn: () => studyApi.deleteLesson(lesson.id),
    onSuccess: refresh,
    onError: (error) => setProblem(problemOf(error)),
  });
  const removeQuestion = useMutation({
    mutationFn: (id: number) => studyApi.deleteQuestion(id),
    onSuccess: refresh,
    onError: (error) => setProblem(problemOf(error)),
  });

  return (
    <Card title={`${number}. ${lesson.title}`}>
      {editing ? (
        <LessonForm
          initial={lesson}
          submitLabel="Сохранить урок"
          onSubmit={async (value) => {
            await studyApi.editLesson(lesson.id, value);
            setEditing(false);
            refresh();
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <p className="faint">
            {lesson.questions.length === 0
              ? 'без теста — засчитывается по прочтении'
              : `тест: вопросов ${lesson.questions.length}`}
            {lesson.has_results ? ' · есть результаты сотрудников' : ''}
          </p>
          <div className="chips">
            <Button small variant="quiet" onClick={() => setEditing(true)}>
              Править урок
            </Button>
            {!lesson.has_results && (
              <Button
                small
                variant="quiet"
                disabled={removeLesson.isPending}
                onClick={() => removeLesson.mutate()}
              >
                Удалить урок
              </Button>
            )}
          </div>
        </>
      )}

      {lesson.questions.map((question, index) =>
        editingQuestion === question.id ? (
          <QuestionForm
            key={question.id}
            initial={question}
            onSubmit={async (value) => {
              await studyApi.editQuestion(question.id, value);
              setEditingQuestion(null);
              refresh();
            }}
            onCancel={() => setEditingQuestion(null)}
          />
        ) : (
          <div key={question.id} className="question-row">
            <p>
              <strong>
                {index + 1}. {question.body}
              </strong>
            </p>
            <ul>
              {question.options.map((option) => (
                <li key={option.body}>
                  {option.is_correct ? '✓ ' : ''}
                  {option.body}
                </li>
              ))}
            </ul>
            <div className="chips">
              <Button small variant="quiet" onClick={() => setEditingQuestion(question.id)}>
                Править
              </Button>
              {question.answers === 0 && (
                <Button
                  small
                  variant="quiet"
                  disabled={removeQuestion.isPending}
                  onClick={() => removeQuestion.mutate(question.id)}
                >
                  Удалить
                </Button>
              )}
            </div>
          </div>
        ),
      )}

      {problem && <p className="demo-notice">{problem}</p>}

      {adding ? (
        <QuestionForm
          onSubmit={async (value) => {
            await studyApi.addQuestion(lesson.id, value);
            setAdding(false);
            refresh();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button small variant="quiet" onClick={() => setAdding(true)}>
          + Вопрос теста
        </Button>
      )}
    </Card>
  );
}

export function CourseContentEditor({
  courseId,
  onChanged,
}: {
  courseId: number;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const key = ['admin-course-content', courseId];
  const content = useQuery({ queryKey: key, queryFn: () => studyApi.courseContent(courseId) });
  const [adding, setAdding] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    onChanged();
  };

  if (content.isPending) return <Loading />;
  if (content.error || !content.data)
    return <ErrorState error={content.error} onRetry={() => content.refetch()} />;

  return (
    <div className="course-content">
      {content.data.lessons.map((lesson, index) => (
        <LessonBlock key={lesson.id} lesson={lesson} number={index + 1} refresh={refresh} />
      ))}
      {adding ? (
        <Card title="Новый урок">
          <LessonForm
            submitLabel="Добавить урок"
            onSubmit={async (value) => {
              await studyApi.addLesson(courseId, value);
              setAdding(false);
              refresh();
            }}
            onCancel={() => setAdding(false)}
          />
        </Card>
      ) : (
        <Button small variant="primary" onClick={() => setAdding(true)}>
          + Урок в конец курса
        </Button>
      )}
    </div>
  );
}
